import { useEffect, useState } from 'react';
import { loadFamilyPortalSlots } from '../data/familyPortal';

// Find & Book's open slots for one studio's family (#773), asked of the
// server (`familyPortalSlots`, functions/portal/book.js). Online only, like
// the view (usePortalView.js), and asked again by `retry` — after a booking,
// or after a refusal that says the slot was just taken.
//
// `{ answer, error, loading, retry }`: `answer` is the server's as it came
// (`{ bookingOn, students, slots, family, studio, today }`), `error` the
// core's sentence and its code. An answer for another family is never
// returned, so switching families never shows the last one's slots.
//
// `reschedule` — `{ studentId, occurrenceId }` — asks instead for the times
// that student may move that lesson to (#543); the answer is kept apart from
// Find & Book's by its key.
export function usePortalSlots(choice, reschedule = null) {
  const moveStudent = reschedule?.studentId ?? null;
  const moveFrom = reschedule?.occurrenceId ?? null;
  const key = choice?.key ? [choice.key, moveStudent, moveFrom].filter(Boolean).join('|') : null;
  const studioUid = choice?.studioUid ?? null;
  const familyId = choice?.familyId ?? null;
  const [state, setState] = useState({ key: null, answer: null, error: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!key) return undefined;
    let live = true;
    setState((prev) => (prev.key === key ? { key, answer: null, error: null } : prev));
    const asked = moveStudent && moveFrom ? { studentId: moveStudent, occurrenceId: moveFrom } : null;
    loadFamilyPortalSlots({ studioUid, familyId, reschedule: asked }).then(
      (answer) => { if (live) setState({ key, answer, error: null }); },
      (err) => {
        if (live) setState({ key, answer: null, error: { message: err?.message || String(err), refusal: err?.refusal || null } });
      },
    );
    return () => { live = false; };
  }, [key, studioUid, familyId, moveStudent, moveFrom, attempt]);

  const mine = state.key === key ? state : { answer: null, error: null };
  return {
    answer: mine.answer,
    error: mine.error,
    loading: Boolean(key) && !mine.answer && !mine.error,
    retry: () => setAttempt((n) => n + 1),
  };
}
